import React from 'react';
import { CheckCircle2, CircleHelp, SearchX } from 'lucide-react';
import { WebsiteFit } from '../../../types/results.types';

interface MatchStrengthCellProps {
  fit?: WebsiteFit | null;
  compact?: boolean;
  shortLabels?: Record<string, string>;
}

interface StrengthTier {
  label: string;
  fitLabel: string;
  barColor: string;
  barWidth: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
}

function strengthTier(score: number): StrengthTier {
  if (score >= 80) return { label: 'Excellent fit', fitLabel: 'Excellent fit', barColor: 'bg-[#D8B26E]', barWidth: 'w-full', textColor: 'text-[#92400E]', bgColor: 'bg-amber-50', borderColor: 'border-amber-200' };
  if (score >= 60) return { label: 'Strong fit', fitLabel: 'Strong fit', barColor: 'bg-emerald-500', barWidth: 'w-3/4', textColor: 'text-emerald-800', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' };
  if (score >= 40) return { label: 'Moderate fit', fitLabel: 'Moderate fit', barColor: 'bg-emerald-300', barWidth: 'w-1/2', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-100' };
  return { label: 'Weak fit', fitLabel: 'Weak fit', barColor: 'bg-amber-200', barWidth: 'w-1/4', textColor: 'text-amber-700', bgColor: 'bg-amber-50', borderColor: 'border-amber-100' };
}

const MatchStrengthCell: React.FC<MatchStrengthCellProps> = ({ fit, compact = false, shortLabels = {} }) => {
  if (!fit) return <span className="text-xs text-gray-400">Not requested</span>;

  const confirmed = fit.delivery_group === 'website_confirmed';
  const unknown = fit.verdict === 'unknown';

  if (compact) {
    const Icon = confirmed ? CheckCircle2 : unknown ? CircleHelp : SearchX;
    const tier = confirmed && fit.fit_score != null ? strengthTier(fit.fit_score) : null;
    const label = tier?.label ?? (confirmed ? 'Website match' : unknown ? "Couldn't verify" : 'Partial match');
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
          confirmed
            ? `${tier?.borderColor ?? 'border-emerald-200'} ${tier?.bgColor ?? 'bg-emerald-50'} ${tier?.textColor ?? 'text-emerald-800'}`
            : unknown
              ? 'border-slate-200 bg-slate-50 text-slate-700'
              : 'border-amber-200 bg-amber-50 text-amber-800'
        }`}
      >
        <Icon className="h-3 w-3" aria-hidden="true" />
        {label}
      </span>
    );
  }

  if (confirmed) {
    const score = fit.fit_score;
    const tier = score != null ? strengthTier(score) : null;
    const allTags = fit.matched_offerings;
    const visibleTags = allTags.slice(0, 2);
    const overflow = allTags.length - 2;
    return (
      <div className="flex flex-col gap-1.5">
        <div className={`inline-flex items-center gap-1.5 self-start rounded-lg border px-2 py-1 ${tier?.borderColor ?? 'border-emerald-200'} ${tier?.bgColor ?? 'bg-emerald-50'}`}>
          <CheckCircle2 className={`h-3.5 w-3.5 flex-shrink-0 ${tier?.textColor ?? 'text-emerald-600'}`} aria-hidden="true" />
          {score != null ? (
            <span className={`text-xs font-bold ${tier?.textColor ?? 'text-emerald-700'}`}>
              {score}/100 · {tier?.fitLabel ?? 'Website match'}
            </span>
          ) : (
            <span className={`text-xs font-bold ${tier?.textColor ?? 'text-emerald-700'}`}>
              {tier?.label ?? 'Website match'}
            </span>
          )}
        </div>
        {score != null && (
          <div className="h-1 w-full rounded-full bg-gray-100">
            <div className={`h-1 rounded-full ${tier?.barColor ?? 'bg-emerald-400'} ${tier?.barWidth ?? 'w-1/2'}`} />
          </div>
        )}
        {visibleTags.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {visibleTags.map((tag) => (
              <span
                key={tag}
                className="inline-block rounded-full border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"
              >
                {shortLabels[tag] ?? tag}
              </span>
            ))}
            {overflow > 0 && (
              <span className="inline-block rounded-full border border-emerald-100 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600">
                +{overflow} more
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  const Icon = unknown ? CircleHelp : SearchX;
  const label = unknown ? "Couldn't verify website" : 'Partial match';
  const sub = unknown ? 'Partial match' : 'Limited website match';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
        unknown
          ? 'border-slate-200 bg-slate-50 text-slate-700'
          : 'border-amber-200 bg-amber-50 text-amber-800'
      }`}
    >
      <Icon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
      <span className="flex flex-col leading-tight">
        <span>{label}</span>
        <span className="text-[10px] font-medium opacity-75">{sub}</span>
      </span>
    </span>
  );
};

export default MatchStrengthCell;
